import { HttpRequest, IHttpClient } from "@features/shared/request/model";

const SERVER_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

export interface ProfileField<T> {
    confident: boolean;
    value: T;
}

export interface AiProficiency {
    confident: boolean;
    description: string;
    level: number;
}

export interface PersonalProfile {
    ai_proficiency: AiProficiency;
    goal: ProfileField<string>;
    name: ProfileField<string>;
    suggested_fields: ProfileField<string[]>;
    suggested_professions: ProfileField<string[]>;
    suggested_tools: ProfileField<string[]>;
    edit_summary?: string;
}

export interface PersonalProfileResponse {
    error: any;
    profile: PersonalProfile;
}

export interface LearningPlanStep {
    courseId: number;
    courseLevel: string;
    courseName: string;
    topicId: number;
    why: string;
}

export type LearningPlan = {
    how?: string;
    edit_summary?: string;
    planGoal?: string;
    planSteps?: LearningPlanStep[];
    planTitle?: string;
    professions?: string[];
    results?: string[];
    tools_learned?: string[];
    error?: string;
};

export type LearningPlanErrorResponse = {
    error: any;
};

export interface LearningPlanResponse {
    error: any;
    plan: LearningPlan;
}

export type LearningPlanApiResponse = LearningPlanResponse | LearningPlanErrorResponse;

export class TStudentPersonalApi {
    constructor(private httpClient: IHttpClient) {}

    async getPersonalProfile(): Promise<PersonalProfileResponse> {
        return await this.httpClient.do<PersonalProfileResponse>(`${SERVER_URL}/student/learning-profile`, "GET");
    }

    async generatePersonalProfile(): Promise<PersonalProfileResponse> {
        return await this.httpClient.do<PersonalProfileResponse>(`${SERVER_URL}/student/learning-profile`, "POST");
    }

    async editPersonalProfile(editPrompt: string): Promise<PersonalProfileResponse> {
        return await this.httpClient.do<PersonalProfileResponse>(`${SERVER_URL}/student/learning-profile`, "PUT", {
            edit_prompt: editPrompt
        });
    }

    async getLearningPlan(): Promise<LearningPlanResponse> {
        return await this.httpClient.do<LearningPlanResponse>(`${SERVER_URL}/student/personal-plan`, "GET");
    }

    async getCanGenerateLearningPlan() {
        return await this.httpClient.do<{ canGenerate: boolean }>(
            `${SERVER_URL}/student/learning-profile/can-generate`
        );
    }

    async generateLearningPlan(): Promise<LearningPlanResponse> {
        return await this.httpClient.do<LearningPlanResponse>(`${SERVER_URL}/student/personal-plan`, "POST");
    }

    async editLearningPlan(editPrompt: string): Promise<LearningPlanResponse> {
        return await this.httpClient.do<LearningPlanResponse>(`${SERVER_URL}/student/personal-plan`, "PUT", {
            edit_prompt: editPrompt
        });
    }
}

export const StudentPersonalApi = new TStudentPersonalApi(HttpRequest);
